import { NumberInputProps } from "@mantine/core";
import isNil from "lodash/isNil";
import { useState } from "react";
import { Control, FieldValues, Path, PathValue } from "react-hook-form";

import { PriceInputController } from "#shared/view/components/PriceInputController";
import { InputRightSection } from "#shared/view/designSystem/inputs/InputRightSection";
import { Factor, UnitName } from "#shared/view/designSystem/inputs/units";
import { useRecommendedInputStyles } from "#shared/view/designSystem/inputs/useRecommendedInputStyles";
import { useRecommendedValue } from "#shared/view/designSystem/inputs/useRecommendedValue";

interface Props<T extends FieldValues> extends NumberInputProps {
  name: Path<T>;
  label: string;
  onClickWhy?: () => void;
  onChange?: (value: string | number) => void;
  onChangeWithComputedValue?: (value: string | number) => void;
  control: Control<T>;
  required?: boolean;
  defaultInputValue?: number;
  computedValue?: { value: PathValue<T, Path<T>> | null; message?: string };
  companyValue?: { value: PathValue<T, Path<T>> | null; message?: string };
  unit?: UnitName;
  onBlur?: () => void;
  unitFactors?: { value: number; unit: UnitName }[];
  defaultUnitFactor?: { value: number; unit: UnitName };
  dependencies?: Path<T>[];
  onChangeFactor?: (unit?: UnitName) => void;
  precision?: number;
}

export const RecommendedPriceInputController = <T extends FieldValues>({
  name,
  control,
  label,
  onClickWhy,
  onChange,
  placeholder,
  required,
  disabled,
  computedValue,
  companyValue,
  defaultInputValue,
  unit,
  onBlur,
  unitFactors,
  dependencies,
  defaultUnitFactor,
  onChangeFactor,
  onChangeWithComputedValue,
  precision = 2,
}: Props<T>) => {
  const styles = useRecommendedInputStyles({
    companyValue,
    computedValue,
    name,
    precision: precision,
  });

  useRecommendedValue({ name, companyValue, computedValue, dependencies });

  const [selectedFactor, setSelectedFactor] = useState<Factor>(
    defaultUnitFactor || { value: 1, unit },
  );

  const handleChangeFactor = (factor: Factor) => {
    setSelectedFactor(factor);
    if (onChangeFactor) {
      onChangeFactor(factor.unit);
    }
  };

  return (
    <PriceInputController
      control={control}
      name={name}
      label={label}
      unit={selectedFactor.unit}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      onClickWhy={onClickWhy}
      onChange={onChange}
      onChangeWithComputedValue={onChangeWithComputedValue}
      onBlur={onBlur}
      factor={selectedFactor.value}
      defaultValue={defaultInputValue}
      rightSectionWidth={"auto"}
      rightSection={
        (unitFactors || !isNil(companyValue) || !isNil(computedValue)) && (
          <InputRightSection
            companyValue={companyValue}
            computedValue={computedValue}
            name={name}
            unit={unit}
            unitFactors={unitFactors}
            setSelectedFactor={handleChangeFactor}
            precision={precision}
          />
        )
      }
      styles={styles}
    />
  );
};
